import { useState, useEffect, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import IframeViewer from "../urlOpener/IFrameViewer";
import RiskSkeleton from "./RiskSkeleton";

interface Rating {
  task: string;
  rating: string;
  reasoning: string;
}

interface ApiResponse {
  ratings: Rating[];
}

interface RiskFactor {
  id: number;
  name: string;
  weight: string;
  score: number;
  description: string;
  result: string;
  resultScore: number;
  reasoning: string;
  macroFactors: {
    title: string;
    likelihood: string;
    impact: string;
    notes: string[];
  }[];
}

interface RiskAssessmentCardProps {
  id?: string;
  title: string;
  apiResponse: ApiResponse; // API data for this category
  getWeightForTask: (taskName: string) => string; // custom weight mapping function
}

// Helper functions
const getRiskResultLabel = (rating: number): string => {
  if (rating <= 2) return "Low Risk";
  if (rating <= 4) return "Moderate Risk";
  if (rating <= 6) return "Above Average Risk";
  if (rating <= 8) return "High Risk";
  return "Critical Risk";
};

const getLikelihoodFromRating = (rating: number): string => {
  if (rating <= 3) return "Unlikely";
  if (rating <= 5) return "Possible";
  if (rating <= 7) return "Likely";
  return "Highly Likely";
};

const getImpactFromRating = (rating: number): string => {
  if (rating <= 2) return "Low";
  if (rating <= 4) return "Moderate";
  if (rating <= 6) return "High";
  return "Severe";
};

const countRisksByCategory = (riskData: RiskFactor[]) => {
  return riskData.reduce(
    (acc, risk) => {
      if (risk.score <= 3) {
        acc.low += 1;
      } else if (risk.score >= 4 && risk.score <= 6) {
        acc.medium += 1;
      } else if (risk.score >= 7) {
        acc.critical += 1;
      }
      return acc;
    },
    { critical: 0, medium: 0, low: 0 }
  );
};


// Convert API response to RiskFactor[]
const convertApiResponseToRiskFactors = (
  apiResponse: ApiResponse,
  getWeightForTask: (taskName: string) => string
): RiskFactor[] => {
  return apiResponse.ratings.map((rating, index) => ({
    id: index + 1,
    name: rating.task,
    weight: `${getWeightForTask(rating.task)} weight`,
    score: parseInt(rating.rating),
    description: `AI analysis concluded that ${rating.task} can be classified based on current market conditions and forecasts.`,
    result: getRiskResultLabel(parseInt(rating.rating)),
    resultScore: parseInt(rating.rating),
    reasoning: rating.reasoning,
    macroFactors: [
      {
        title: rating.task,
        likelihood: getLikelihoodFromRating(parseInt(rating.rating)),
        impact: getImpactFromRating(parseInt(rating.rating)),
        notes: [rating.reasoning],
      },
    ],
  }));
};

export default function RiskAssessmentCard({
  title,
  apiResponse,
  getWeightForTask,
}: RiskAssessmentCardProps) {
  const [selectedRisk, setSelectedRisk] = useState<RiskFactor | null>(null);
  const [loading, setLoading] = useState(true);
  const [riskData, setRiskData] = useState<RiskFactor[]>([]);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [showScoringCriteria, setShowScoringCriteria] = useState(false);

  const riskCounts = useMemo(() => countRisksByCategory(riskData), [riskData]);

  useEffect(() => {
    setTimeout(() => {
      const converted = convertApiResponseToRiskFactors(
        apiResponse,
        getWeightForTask
      );
      setRiskData(converted);
      setSelectedRisk(converted[0]);
      setLoading(false);
    }, 1000);
  }, [apiResponse, getWeightForTask]);

  // const gestWeightNum = (w: string) => Number(w.replace(/[^0-9.]/g, ""));
  // const totalWeight = riskData.reduce(
  //   (sum, r) => sum + getWeightNum(r.weight),
  //   0
  // );

  // const totalScore =
  //   riskData.length > 0 && totalWeight > 0
  //     ? riskData.reduce((sum, r) => {
  //         return (
  //           sum +
  //           (isNaN(r.score) ? 0 : r.score) * (getWeightNum(r.weight) / 100)
  //         );
  //       }, 0)
  //     : 0;

  // const weightedScore = totalWeight / riskData.length;

  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setIframeUrl(url);
  };

  // Show loading skeleton
  if (loading) {
    return <RiskSkeleton />;
  }

  return (
    <div className="bg-[#FDFDFD] rounded-2xl shadow w-full max-w-4xl mx-auto mt-4 2xl:mt-20  ">
      {/* Header */}
      <div className="flex  items-center border-b py-3 border-gray-200 bg-gray-50 justify-between">
        <h2 className="text-3xl font-light text-gray-700 capitalize ml-4">
          {title}
        </h2>
        <div className="flex items-center gap-2 mr-4">
          {/* Critical */}
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-red-200 border-2 border-red-400"></div>
            <span className="text-gray-700">
              Critical <span className="font-medium">({riskCounts.critical})</span>
            </span>
          </div>

          {/* Medium */}
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-yellow-200 border-2 border-yellow-400"></div>
            <span className="text-gray-700">
              Medium <span className="font-medium">({riskCounts.medium})</span>
            </span>
          </div>

          {/* Low */}
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-green-200 border-2 border-green-400"></div>
            <span className="text-gray-700">
              Low <span className="font-medium">({riskCounts.low})</span>
            </span>
          </div>
        </div>
      </div>

      {/* Score Row */}
      {/* <div className="flex items-center justify-between border-b p-4 border-gray-200 gap-4">
        <div className="flex items-center gap-4">
          <div className="text-3xl text-green-700 bg-green-50 px-6 py-2 rounded-full border border-gray-200">
            {totalScore.toFixed(1)}
            <span className="text-gray-400 ml-2"></span>
          </div>
          <span className="text-gray-700">Cumulative risk score</span>
        </div>
        <span className="text-md text-gray-700">
          {weightedScore.toFixed(1)}% weightage
        </span>
      </div> */}

      {/* Table Header */}
      <div className="w-full p-4 border-b border-gray-200 text-sm text-gray-600">
        <div className="flex justify-between  w-[22vw] lg:w-[18vw] 2xl:w-[13w]">
          <span>Risk Factor</span>
          <span>Risk Score</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-5 gap-4">
        {/* Left column */}
        <div className="col-span-2 border-r border-gray-300 px-0.5  ">
          {riskData.map((risk) => (
            <div
              key={risk.id}
              className={`flex justify-between items-center py-3 px-2  cursor-pointer transition ${selectedRisk?.id === risk.id
                ? "bg-gray-50 border-l-4  border-blue-400 shadow-xs"
                : "hover:bg-gray-50 border-b-2 border-gray-200 "
                }`}
              onClick={() => setSelectedRisk(risk)}
            >
              <div>
                <div className=" text-black text-md font-medium">{risk.name}</div>
                <div className="text-xs text-gray-500">{
                  (100 / riskData.length).toFixed(2)}% weight
                </div>
              </div>
              <div
                className={`text-sm font-semibold ml-2 px-2 py-1 mr-8 rounded-lg shadow-xs border text-gray-600 border-gray-200
              ${risk.score <= 3 ? "text-green-600" : ""}
              ${risk.score >= 4 && risk.score <= 6 ? "text-yellow-600" : ""}
              ${risk.score >= 7 ? "  text-red-600" : ""}
            `}
              >
                {risk.score}
              </div>
            </div>
          ))}
        </div>

        {/* Right column */}
        <div className="col-span-3 p-4  ">
          {selectedRisk && (
            <>
              <p className="text-gray-600 pb-4 mb-2 border-b border-gray-200 font-medium">
                Analysis and Key Factors
              </p>
              <p className="text-gray-600 mb-3">{selectedRisk.description}</p>

              {/* Result box */}
              <div className="bg-gray-100 rounded-xl p-3  ">
                <div className="flex justify-between items-center text-sm pb-2">
                  <div>
                    <div className="text-sm text-gray-500">RESULT</div>
                    <div className="text-gray-700">{selectedRisk.result}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">SCORE</div>
                    <div className="text-gray-700 ml-3">
                      {selectedRisk.resultScore}
                    </div>
                  </div>
                </div>
                {/* <div className="border-t border-gray-300 pt-2">
                  <div
                    className="text-gray-500 text-sm text-center cursor-pointer hover:text-black transition-colors"
                    onClick={() => setShowScoringCriteria(true)}
                  >
                    View Scoring Criteria
                  </div>
                </div> */}
              </div>

              {/* Factors */}
              <div>
                <p className="text-gray-600  my-2 ">
                  Key factors that influenced this scoring:
                </p>
                {selectedRisk.macroFactors.map((factor, idx) => (
                  <div key={idx} className="mb-4">
                    <h4 className="font-semibold text-gray-600">
                      {factor.title}
                    </h4>

                    <div className="bg-gray-100 rounded-xl p-3 my-4 ">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-md text-gray-500">
                            LIKELIHOOD
                          </div>
                          <div className="text-gray-700 text-sm">
                            {factor.likelihood}
                          </div>
                        </div>
                        <div>
                          <div className="text-md text-gray-500">IMPACT</div>
                          <div className="text-gray-700 text-sm">
                            {factor.impact}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Supporting Evidence */}
                    <div className="text-sm text-gray-600">
                      <h5 className="font-medium mb-1">Supporting Evidence:</h5>
                      <ul className="list-disc pl-6">
                        {factor.notes.map((note, i) => (
                          <li key={i} className="mb-1">
                            <ReactMarkdown
                              components={{
                                p: ({ children }) => {
                                  const nodes = Array.isArray(children) ? children : [children];

                                  return (
                                    <p className="text-black mb-3">
                                      {nodes.map((node, index) => {
                                        if (typeof node === "string") {
                                          return node
                                            .split(/(?<!\d)\.(?!\d)/)
                                            .map((part, i, arr) => (
                                              <span key={`${index}-${i}`}>
                                                {part}
                                                {i < arr.length - 1 && (
                                                  <>
                                                    .<br />
                                                    <br />
                                                  </>
                                                )}
                                              </span>
                                            ));
                                        }

                                        return <span key={index}>{node}</span>;
                                      })}
                                    </p>
                                  );
                                },
                                a: ({ href, children }) => {
                                  let pageNum: string | null = null;

                                  try {
                                    if (href) {
                                      const hash = new URL(href).hash;
                                      pageNum = hash.includes("=")
                                        ? hash.split("=")[1]
                                        : null;
                                    }
                                  } catch {
                                    pageNum = null;
                                  }

                                  if (!pageNum && children) {
                                    pageNum = String(children);
                                  }

                                  return (
                                    <button
                                      onClick={(e) => handleSourceClick(e, href || "")}
                                      className="text-black text-xs font-thin cursor-pointer hover:underline"
                                    >
                                      [{pageNum || "?"}]
                                    </button>
                                  );
                                },
                              }}
                            >
                              {note}
                            </ReactMarkdown>

                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Scoring Criteria Modal */}
      {showScoringCriteria && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity duration-300"
          onClick={() => setShowScoringCriteria(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden animate-in fade-in-0 zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">
              <h3 className="text-lg  text-gray-700 tracking-tight">
                Risk Rating Scale
              </h3>
              <button
                onClick={() => setShowScoringCriteria(false)}
                className="text-gray-400 cursor-pointer hover:text-gray-600 text-xl w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5 space-y-4">
              {[
                {
                  label: "1–3: Low Risk",
                  desc: "Minimal impact, very unlikely",
                  colors:
                    "from-green-50 to-green-100 border-green-500 text-green-800",
                  dot: "text-green-500",
                },
                {
                  label: "4–6: Moderate Risk",
                  desc: "Some impact, possible occurrence",
                  colors:
                    "from-yellow-50 to-yellow-100 border-yellow-500 text-yellow-800",
                  dot: "text-yellow-500",
                },
                {
                  label: "7–10: High/Critical Risk",
                  desc: "Significant to severe impact, highly likely",
                  colors: "from-red-50 to-red-100 border-red-500 text-red-800",
                  dot: "text-red-500",
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className={`flex items-start justify-between p-4 bg-gradient-to-r ${item.colors} border-l-4 rounded-xl shadow-sm hover:shadow transition`}
                >
                  <div>
                    <p className={`font-medium ${item.colors.split(" ")[2]}`}>
                      {item.label}
                    </p>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </div>
                  <span className={`text-lg ${item.dot}`}>●</span>
                </div>
              ))}

              {/* Footer Note */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-sm text-gray-700 text-center leading-relaxed">
                  Risk scores are based on{" "}
                  <span className="font-medium text-gray-900">
                    AI-driven analysis
                  </span>{" "}
                  of market data, historical patterns, and forecasts, providing
                  objective insights for investment decision-making.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {iframeUrl && (
        <IframeViewer url={iframeUrl} onClose={() => setIframeUrl(null)} />
      )}
    </div>
  );
}
